import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { vaultApi } from '../services/api';
import { haptic } from '../utils/haptics';
import {
  buildSetupPayload, tryUnlock, encryptJson, decryptJson,
  generateRecoveryKey, wrapPassphraseWithRecoveryKey, unwrapPassphraseWithRecoveryKey,
  generateTransferSecret, wrapPassphraseForTransfer, unwrapPassphraseFromTransfer,
} from '../services/vaultCrypto';
import { useAuth } from './AuthContext';
import {
  getBiometricAvailability, isVaultBiometricSaved, saveVaultBiometric,
  getVaultBiometric, clearVaultBiometric, biometryLabel,
} from '../utils/biometricAuth';

/**
 * VaultContext — holds the derived vault key in memory for the current
 * session. Key is deliberately NEVER persisted (no localStorage / no
 * cookies) so a stolen laptop only leaks vault items if the vault is
 * actively unlocked.
 *
 * Additions in this iteration:
 *   • Auto-lock after N minutes of inactivity (configurable, per-user
 *     localStorage — the timeout itself is not sensitive).
 *   • Recovery key generation + wrap/unwrap (client-side crypto with
 *     server storing only the ciphertext blob).
 *   • Face ID / fingerprint unlock (native apps): the passphrase is kept in
 *     the phone's Keychain / Keystore, released only after a live
 *     biometric check — see utils/biometricAuth.js.
 *   • Reset by emailed code when both passphrase and recovery key are lost.
 */

const VaultContext = createContext(null);

const AUTOLOCK_STORAGE_KEY = 'tofbox_vault_autolock_min_v1';
const DEFAULT_AUTOLOCK_MIN = 15;
export const AUTOLOCK_OPTIONS = [5, 10, 15, 30, 0]; // 0 = never

// Plugin error codes for a prompt the user (or the OS) dismissed — not worth a toast.
const BIO_DISMISSED = ['11', '15', '16', '17'];
const NO_BIO = { available: false, type: 0, enabled: false };

function loadAutolockMin() {
  try {
    const raw = localStorage.getItem(AUTOLOCK_STORAGE_KEY);
    if (raw === null) return DEFAULT_AUTOLOCK_MIN;
    const n = parseInt(raw, 10);
    return AUTOLOCK_OPTIONS.includes(n) ? n : DEFAULT_AUTOLOCK_MIN;
  } catch { return DEFAULT_AUTOLOCK_MIN; }
}

function saveAutolockMin(n) {
  try { localStorage.setItem(AUTOLOCK_STORAGE_KEY, String(n)); } catch { /* ignore */ }
}

export function useVault() {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used within a VaultProvider');
  return ctx;
}

export function VaultProvider({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [key, setKey] = useState(null);
  const [status, setStatus] = useState({ loading: true, enabled: false, setupAt: null, recoveryEnabled: false });
  const [autolockMin, setAutolockMinState] = useState(loadAutolockMin());
  const [bio, setBio] = useState(NO_BIO);

  // Cache the plaintext passphrase for the current session (in-memory
  // only, cleared on lock). Needed so the user can enable/regenerate a
  // recovery key at any time without being re-prompted.
  const passphraseRef = useRef(null);
  // Inactivity timer — reset on any activity while the vault is unlocked.
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  const scheduleAutolock = useCallback(() => {
    clearTimer();
    if (!key || autolockMin <= 0) return;
    timerRef.current = setTimeout(() => {
      setKey(null);
      passphraseRef.current = null;
    }, autolockMin * 60 * 1000);
  }, [key, autolockMin, clearTimer]);

  // Reset the timer on any activity while unlocked.
  useEffect(() => {
    if (!key) { clearTimer(); return undefined; }
    scheduleAutolock();
    const bump = () => scheduleAutolock();
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      clearTimer();
    };
  }, [key, scheduleAutolock, clearTimer]);

  const setAutolockMin = useCallback((n) => {
    const clean = AUTOLOCK_OPTIONS.includes(n) ? n : DEFAULT_AUTOLOCK_MIN;
    saveAutolockMin(clean);
    setAutolockMinState(clean);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus({ loading: false, enabled: false, setupAt: null, recoveryEnabled: false });
      setKey(null);
      passphraseRef.current = null;
      return;
    }
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await vaultApi.status();
      setStatus({
        loading: false,
        enabled: !!data.enabled,
        setupAt: data.setup_at || null,
        recoveryEnabled: !!data.recovery_enabled,
        recoveryEnabledAt: data.recovery_enabled_at || null,
        _salt: data.salt_b64,
        _verifierIv: data.verifier_iv_b64,
        _verifierCt: data.verifier_ct_b64,
      });
      if (!data.enabled) { setKey(null); passphraseRef.current = null; }
    } catch {
      setStatus({ loading: false, enabled: false, setupAt: null, recoveryEnabled: false });
      setKey(null);
      passphraseRef.current = null;
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Whether this phone can (and does) open the vault with Face ID /
  // fingerprint. Always "unavailable" on the web.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setBio(NO_BIO); return; }
      if (status.loading) return;
      const { isAvailable, biometryType } = await getBiometricAvailability();
      const enabled = isAvailable && status.enabled && await isVaultBiometricSaved(user.user_id);
      if (!cancelled) setBio({ available: !!isAvailable, type: biometryType, enabled: !!enabled });
    })();
    return () => { cancelled = true; };
  }, [user, status.loading, status.enabled]);

  const setup = useCallback(async (passphrase) => {
    if (!user) throw new Error('Not signed in');
    const { salt_b64, verifier_iv_b64, verifier_ct_b64, key: freshKey } =
      await buildSetupPayload(passphrase, user.user_id);
    await vaultApi.setup({ salt_b64, verifier_iv_b64, verifier_ct_b64 });
    setKey(freshKey);
    passphraseRef.current = passphrase;
    await refresh();
  }, [user, refresh]);

  const unlock = useCallback(async (passphrase) => {
    if (!user) throw new Error('Not signed in');
    if (!status.enabled || !status._salt) throw new Error('Vault not initialised');
    const { ok, key: unlocked } = await tryUnlock({
      passphrase,
      salt_b64: status._salt,
      verifier_iv_b64: status._verifierIv,
      verifier_ct_b64: status._verifierCt,
      userId: user.user_id,
    });
    if (!ok) {
      haptic('error');
      return false;
    }
    setKey(unlocked);
    passphraseRef.current = passphrase;
    haptic('success');
    return true;
  }, [status, user]);

  const lock = useCallback(() => {
    setKey(null);
    passphraseRef.current = null;
    clearTimer();
  }, [clearTimer]);

  const disableBiometric = useCallback(async () => {
    await clearVaultBiometric();
    setBio((b) => ({ ...b, enabled: false }));
  }, []);

  /** Store the current passphrase behind Face ID / fingerprint on this
   *  phone. Needs the vault unlocked (passphrase in memory). */
  const enableBiometric = useCallback(async () => {
    if (!user || !key || !passphraseRef.current) throw new Error(t('vault.errors.locked'));
    await saveVaultBiometric(user.user_id, passphraseRef.current);
    setBio((b) => ({ ...b, enabled: true }));
  }, [user, key, t]);

  /** Face ID / fingerprint → passphrase → unlock. Toasts the outcome
   *  (nothing when the prompt was dismissed) and resolves to a boolean. */
  const unlockWithBiometric = useCallback(async () => {
    if (!user) throw new Error('Not signed in');
    const method = biometryLabel(bio.type, t);
    let stored;
    try {
      stored = await getVaultBiometric(t('vault.bio.reason'));
    } catch (e) {
      const code = String(e?.code ?? '');
      if (BIO_DISMISSED.includes(code)) return false;
      if (code === '21') {
        // Faces / fingerprints changed on the phone: the OS wiped the entry.
        await disableBiometric();
        toast.info(t('vault.bio.invalidated', { method }));
      } else {
        toast.error(t('vault.bio.error', { method }));
      }
      return false;
    }
    if (stored.userId !== user.user_id || !(await unlock(stored.passphrase))) {
      // Passphrase changed (or vault reset) from another device.
      await disableBiometric();
      toast.info(t('vault.bio.stale', { method }));
      return false;
    }
    toast.success(t('vault.unlockOk'));
    return true;
  }, [user, bio.type, unlock, disableBiometric, t]);

  const encrypt = useCallback(async (obj) => {
    if (!key) throw new Error('Vault is locked');
    return encryptJson(key, obj);
  }, [key]);

  const decrypt = useCallback(async (iv_b64, ct_b64) => {
    if (!key) throw new Error('Vault is locked');
    return decryptJson(key, iv_b64, ct_b64);
  }, [key]);

  /** Rotate the vault passphrase — see original comments in git history.
   *  When a recovery key is enabled, the wrapped-passphrase blob is
   *  invalidated (server-side DELETE) and the user is prompted to
   *  regenerate a new recovery key. */
  const rotatePassphrase = useCallback(async (newPassphrase, onProgress) => {
    if (!user) throw new Error('Not signed in');
    if (!key) throw new Error('Vault is locked — unlock first with the current passphrase.');
    if (!newPassphrase || newPassphrase.length < 8) {
      throw new Error(t('vault.errors.passwordTooShort'));
    }
    const { data } = await vaultApi.listItems();
    const items = data?.items || [];
    const total = items.length;
    onProgress?.({ done: 0, total });
    const plaintexts = [];
    for (let i = 0; i < items.length; i += 1) {
      const plain = await decryptJson(key, items[i].vault_iv_b64, items[i].vault_ct_b64);
      plaintexts.push({ id: items[i].id, plain });
    }
    const setupPayload = await buildSetupPayload(newPassphrase, user.user_id);
    for (let i = 0; i < plaintexts.length; i += 1) {
      const { iv_b64, ct_b64 } = await encryptJson(setupPayload.key, plaintexts[i].plain);
      await vaultApi.reencryptItem(plaintexts[i].id, { iv_b64, ct_b64 });
      onProgress?.({ done: i + 1, total });
    }
    await vaultApi.setup({
      salt_b64: setupPayload.salt_b64,
      verifier_iv_b64: setupPayload.verifier_iv_b64,
      verifier_ct_b64: setupPayload.verifier_ct_b64,
    });
    // The wrapped passphrase (recovery blob) is now stale — nuke it so
    // we don't leave a dangling recovery key that no longer works.
    if (status.recoveryEnabled) {
      try { await vaultApi.disableRecovery(); } catch { /* best effort */ }
    }
    setKey(setupPayload.key);
    passphraseRef.current = newPassphrase;
    // Keep Face ID working on this phone with the new passphrase.
    let bioDropped = false;
    if (bio.enabled) {
      try { await saveVaultBiometric(user.user_id, newPassphrase); }
      catch { await disableBiometric(); bioDropped = true; }
    }
    await refresh();
    return { rotated: total, bioDropped };
  }, [user, key, refresh, status.recoveryEnabled, bio.enabled, disableBiometric, t]);

  /** Generate a fresh recovery key + wrap the CURRENT passphrase with it.
   *  Requires the vault to be unlocked (we need the passphrase in memory).
   *  Returns the plaintext recovery key so the UI can display it ONCE. */
  const enableRecovery = useCallback(async () => {
    if (!user) throw new Error('Not signed in');
    if (!key || !passphraseRef.current) {
      throw new Error(t('vault.errors.unlockForRecoveryKey'));
    }
    const recoveryKey = generateRecoveryKey();
    const { salt_b64, iv_b64, ct_b64 } = await wrapPassphraseWithRecoveryKey(
      passphraseRef.current, recoveryKey,
    );
    await vaultApi.enableRecovery({ salt_b64, iv_b64, ct_b64 });
    await refresh();
    return recoveryKey;
  }, [user, key, refresh, t]);

  /** Recover the passphrase using a printed recovery key, then unlock. */
  const unlockWithRecoveryKey = useCallback(async (recoveryKey) => {
    if (!user) throw new Error('Not signed in');
    if (!status.enabled) throw new Error('Vault not initialised');
    let blob;
    try { const { data } = await vaultApi.recoveryBlob(); blob = data; }
    catch { throw new Error(t('vault.errors.noRecoveryKey')); }
    let passphrase;
    try {
      passphrase = await unwrapPassphraseWithRecoveryKey({
        recoveryKey, salt_b64: blob.salt_b64, iv_b64: blob.iv_b64, ct_b64: blob.ct_b64,
      });
    } catch {
      throw new Error(t('vault.errors.invalidRecoveryKey'));
    }
    const ok = await unlock(passphrase);
    return ok ? { ok: true, passphrase } : { ok: false };
  }, [user, status.enabled, unlock, t]);

  const disableRecovery = useCallback(async () => {
    await vaultApi.disableRecovery();
    await refresh();
  }, [refresh]);

  /** Email a reset code. Resolves to { items } — how many encrypted items
   *  the reset would delete. */
  const requestReset = useCallback(async () => {
    const { data } = await vaultApi.resetRequest();
    return data;
  }, []);

  /** Wipe the vault (its encrypted items included) once the emailed code
   *  checks out. Resolves to { deleted }. */
  const confirmReset = useCallback(async (code) => {
    const { data } = await vaultApi.resetConfirm(code);
    await disableBiometric();
    lock();
    await refresh();
    return data;
  }, [disableBiometric, lock, refresh]);

  /** Device A — start a cross-device transfer. Requires the vault to be
   *  unlocked (we need the passphrase in memory to re-encrypt it under
   *  a fresh 128-bit secret). Returns { sessionId, secretB64, ttlSeconds }
   *  — the tuple to encode in a QR for device B to scan. */
  const createTransferSession = useCallback(async () => {
    if (!key || !passphraseRef.current) {
      throw new Error(t('vault.errors.unlockForTransferQr'));
    }
    const secretB64 = generateTransferSecret();
    const wrapped = await wrapPassphraseForTransfer(passphraseRef.current, secretB64);
    const { data } = await vaultApi.createTransfer(wrapped);
    return {
      sessionId: data.session_id,
      secretB64,
      ttlSeconds: data.ttl_seconds,
    };
  }, [key, t]);

  /** Device A — cancel a pending transfer (e.g. modal closed before scan). */
  const cancelTransferSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try { await vaultApi.cancelTransfer(sessionId); } catch { /* best-effort */ }
  }, []);

  /** Device B — consume a scanned transfer QR: fetch the encrypted blob,
   *  decrypt it locally with the secret from the QR, and unlock the vault
   *  with the recovered passphrase. */
  const consumeTransferSession = useCallback(async (sessionId, secretB64) => {
    if (!sessionId || !secretB64) throw new Error('QR invalide');
    if (!user) throw new Error('Not signed in');
    let blob;
    try {
      const { data } = await vaultApi.consumeTransfer(sessionId);
      blob = data;
    } catch (e) {
      const s = e?.response?.status;
      if (s === 410) throw new Error(t('vault.errors.transferExpired'));
      if (s === 404) throw new Error(t('vault.errors.transferNotFound'));
      throw new Error(t('vault.errors.transferFetchError'));
    }
    let passphrase;
    try {
      passphrase = await unwrapPassphraseFromTransfer({
        secretB64,
        salt_b64: blob.salt_b64,
        iv_b64: blob.iv_b64,
        ct_b64: blob.ct_b64,
      });
    } catch {
      throw new Error(t('vault.errors.transferDecryptError'));
    }
    const ok = await unlock(passphrase);
    if (!ok) throw new Error(t('vault.errors.transferMismatch'));
    return true;
  }, [user, unlock, t]);

  const value = useMemo(() => ({
    ...status,
    unlocked: !!key,
    autolockMin,
    setAutolockMin,
    setup,
    unlock,
    lock,
    encrypt,
    decrypt,
    rotatePassphrase,
    enableRecovery,
    disableRecovery,
    unlockWithRecoveryKey,
    bio,
    enableBiometric,
    disableBiometric,
    unlockWithBiometric,
    requestReset,
    confirmReset,
    createTransferSession,
    cancelTransferSession,
    consumeTransferSession,
    refresh,
  }), [status, key, autolockMin, setAutolockMin, setup, unlock, lock, encrypt, decrypt,
      rotatePassphrase, enableRecovery, disableRecovery, unlockWithRecoveryKey,
      bio, enableBiometric, disableBiometric, unlockWithBiometric, requestReset, confirmReset,
      createTransferSession, cancelTransferSession, consumeTransferSession, refresh]);

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}
